import { Alert, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'

import { useShippingFeeRefund } from './useShippingFeeRefund'
import { Popup } from '@/components/popup'

interface ShippingFeeRefundModalProps {
  orderId: string
  open: boolean
  onClose: () => void
}

interface ShippingFeeRefundFormValues {
  reason?: string
}

export function ShippingFeeRefundModal({ orderId, open, onClose }: ShippingFeeRefundModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<ShippingFeeRefundFormValues>()

  const { shippingFeeRefund, isRefunding } = useShippingFeeRefund(() => {
    form.resetFields()
    onClose()
  })

  const handleOk = () => {
    void form.validateFields().then((values) => {
      shippingFeeRefund({ orderId, reason: values.reason?.trim() || undefined })
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Popup
      data-testid={`shipping-fee-refund-modal-${orderId}`}
      title={t('common.modals.shippingFeeRefundTitle')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isRefunding}
      okText={t('common.buttons.confirm')}
      cancelText={t('common.buttons.cancel')}
      okButtonProps={{ 'data-testid': 'shipping-fee-refund-modal-ok-btn' }}
      cancelButtonProps={{ 'data-testid': 'shipping-fee-refund-modal-cancel-btn' }}
    >
      <Alert
        type="info"
        showIcon
        message={t('common.modals.shippingFeeRefundMessage')}
        description={t('common.modals.shippingFeeRefundDescription')}
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reason"
          label={t('common.modals.shippingFeeRefundReasonLabel')}
          rules={[{ max: 1000, message: t('common.modals.shippingFeeRefundReasonMaxLength') }]}
        >
          <Input.TextArea
            data-testid="shipping-fee-refund-reason-input"
            rows={4}
            placeholder={t('common.modals.shippingFeeRefundReasonPlaceholder')}
            disabled={isRefunding}
          />
        </Form.Item>
      </Form>
    </Popup>
  )
}
